// Ported from horizontal-actions-dashboard/frontend/src/pages/ActionMatrixPage.tsx — the
// largest and most complex page (the editable spreadsheet-style Action Register). The
// source used react-query mutations that called queryClient.setQueryData(["matrix"], data)
// + invalidateQueries() on every successful edit; this app has no react-query / shared
// query cache, so each handler below simply re-sets this component's local `data` state
// from the response the backend returns (every matrix write endpoint returns the full,
// refreshed MatrixResponse). The app-wide invalidateQueries() side effect (making the
// Summary/Terminal Overview pages reflect edits immediately) has no equivalent here — those
// pages simply (re)fetch fresh data whenever they mount, same as the rest of this app.

import { memo, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { FormEvent, MouseEvent } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useMsal } from '@azure/msal-react';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  getMatrix,
  editMatrixCell,
  editMatrixAction,
  addMatrixAction,
  deleteMatrixAction,
  sendActionToJira,
  ApiError,
} from '../api/horizontalActionsApi';
import type { AddActionRequest, MatrixActionOut, MatrixCellOut, MatrixResponse, MatrixTerminalOut } from '../api/horizontalActionsTypes';
import { TerminalsHeader } from './terminals-shared/TerminalsHeader';
import { useDashboardChanges, markOwnSave } from '../hooks/useDashboardChanges';
import { TerminalsLoadingState, TerminalsErrorState } from './terminals-shared/TerminalsStateViews';
import { groupByRegion } from '../utils/horizontalActionsFormat';

// --- Cell coloring (ported verbatim from the source's Excel-fill-color logic) -----------

// Backend returns the source cell's solid-fill ARGB hex (e.g. "FFA3DCAF"); CSS only
// needs the trailing RGB component.
function fillColorToCss(argbHex: string | null | undefined): string | undefined {
  if (!argbHex || argbHex.length < 6) return undefined;
  return `#${argbHex.slice(-6)}`;
}

// Incident Details exactly as the workbook shows them: four labelled lines, one per item.
// An empty item shows "NA", as the sheet does for an action with no incident.
export function incidentDetailsText(action: {
  affected_terminal: string | null;
  incident_number: string | null;
  incident_date: string | null;
  problem_record: string | null;
}): string {
  const value = (v: string | null) => (v && v.trim() ? v.trim() : 'NA');
  return [
    `Terminal affected: ${value(action.affected_terminal)}`,
    `Incident Number: ${value(action.incident_number)}`,
    `Incident Date: ${value(action.incident_date)}`,
    `Problem: ${value(action.problem_record)}`,
  ].join('\n');
}

// The workbook's two fills: "Completed" solid green (also used for "Applicable No") and
// "In progress" solid amber (also used for "Applicable Yes").
const GREEN_FILL_ARGB = 'FFA3DCAF';
const AMBER_FILL_ARGB = 'FFFFC000';

// The workbook's solid-red highlight: a ticket reference that is not really done.
const RED_FILL_ARGB = 'FFFF0000';

// The cell's text drives the colour for every cell, imported or edited, so the same text always
// looks the same: "COMPLETED" and "Applicable No" green, "Applicable Yes" and in-progress amber,
// the unfilled "Applicable (Yes / No)" white. Only when no text rule applies does the workbook's
// own fill colour decide.
export function resolveCellBackground(cell: MatrixCellOut | undefined): string | undefined {
  const raw = (cell?.raw_value ?? '').trim().toLowerCase();
  if (raw === 'applicable (yes / no)') return '#ffffff'; // No Fill
  if (raw === 'completed') return fillColorToCss(GREEN_FILL_ARGB);
  if (raw.startsWith('applicable no')) return fillColorToCss(GREEN_FILL_ARGB);
  if (raw.startsWith('applicable yes')) return fillColorToCss(AMBER_FILL_ARGB);
  // In progress is amber, but a ticket cell keeps the workbook's own fill when it has one.
  if (cell?.normalized_status === 'in_progress') {
    if (cell.task_reference && cell.fill_color && cell.fill_color !== RED_FILL_ARGB) return fillColorToCss(cell.fill_color);
    return fillColorToCss(AMBER_FILL_ARGB);
  }
  if (cell?.task_reference) {
    // A red highlight means "not really done", so it shows as amber; any other fill is kept.
    if (cell.fill_color === RED_FILL_ARGB) return fillColorToCss(AMBER_FILL_ARGB);
    if (cell.fill_color) return fillColorToCss(cell.fill_color);
  }
  // No fill of its own: anything the app reads as completed is green. This covers a ticket
  // reference and the "PTaskxxxx Completed" placeholder from the dropdown, which has no number.
  if (cell?.normalized_status === 'completed' && !cell.fill_color) return fillColorToCss(GREEN_FILL_ARGB);
  return fillColorToCss(cell?.fill_color);
}

// Jira reference for a terminal cell: the Task key as a link when it exists, otherwise the
// sync state. Rendered beside the editable value so the cell colour and input are unchanged.
export function JiraCellLink({ cell }: { cell: MatrixCellOut | undefined }) {
  const status = cell?.jira_sync_status;
  if (cell?.jira_task_key && cell.jira_task_url) {
    return (
      <Box component="a" href={cell.jira_task_url} target="_blank" rel="noopener noreferrer"
        aria-label={`Open Jira task ${cell.jira_task_key} in a new tab`}
        sx={{ display: 'block', fontSize: '15px', fontWeight: 600, color: '#0067b3', mt: '2px' }}>
        {cell.jira_task_key}
      </Box>
    );
  }
  if (status === 'failed') {
    return (
      <Box role="status" sx={{ fontSize: '12px', color: '#b42318', mt: '2px' }}
        title={cell?.jira_sync_error ?? 'Jira sync failed'}>
        Jira sync failed
      </Box>
    );
  }
  if (status === 'pending' || status === 'processing' || status === 'retrying') {
    return (
      <Box role="status" sx={{ fontSize: '12px', color: '#667085', mt: '2px' }}>
        Jira sync pending
      </Box>
    );
  }
  return null;
}

// Suggests the sheet's own common cell values while keeping the cell a free-text input --
// actions still need to carry a specific PTASK/INC number, which no fixed dropdown can
// enumerate up front.
// Dropdown for a fixed list of values. Only listed values can be chosen.
const ChoiceCell = memo(function ChoiceCell({
  value,
  options,
  ariaLabel,
  onCommit,
}: {
  value: string;
  options: string[];
  ariaLabel: string;
  onCommit: (next: string) => void | Promise<unknown>;
}) {
  // Keep a value that is not in the list visible, so existing text is not silently blanked.
  const choices = value && !options.includes(value) ? [value, ...options] : options;
  return (
    <Select
      size="small"
      value={value}
      displayEmpty
      inputProps={{ 'aria-label': ariaLabel }}
      // The Select is controlled by the stored value, so a failed save simply leaves it unchanged.
      onChange={(e) => {
        void Promise.resolve(onCommit(e.target.value as string)).catch(() => undefined);
      }}
      sx={{ minWidth: 140, fontSize: 'inherit', backgroundColor: 'transparent' }}
    >
      <MenuItem value="">
        <em>Select…</em>
      </MenuItem>
      {choices.map((opt) => (
        <MenuItem key={opt} value={opt}>
          {opt}
        </MenuItem>
      ))}
    </Select>
  );
});

// Helper text for date inputs. The server accepts these formats and says so when it refuses one.
const DATE_HINT = 'e.g. 28 June 2026 or 2026-06-28';
const DATE_HINT_ID = 'ha-date-hint';

// Fixed choice lists for the Classification and Category columns. Values are stored as-is,
// so the spelling here must match what the database holds (see the one-off rename).
export const CLASSIFICATION_OPTIONS = ['Tactical', 'Strategic'];
export const CATEGORY_OPTIONS = [
  'Application',
  'Application & Infrastructure',
  'Infrastructure',
  'Power',
  'Network',
  'Security',
  'Database',
  'End user Device',
  'Backups',
  'Cooling',
  'Other',
];

const MATRIX_CELL_VALUE_OPTIONS = [
  'Applicable (Yes / No)',
  'Applicable Yes',
  'Applicable No',
  'COMPLETED',
  'PTaskxxxx In Progress',
  'PTaskxxxx Completed',
];

const EditableTextCell = memo(function EditableTextCell({
  value,
  ariaLabel,
  backgroundColor,
  onCommit,
  width,
  suggestions,
  hint,
}: {
  value: string;
  ariaLabel: string;
  /** Helper text shown as a tooltip and read out by screen readers. */
  hint?: string;
  backgroundColor?: string;
  // May return a promise. A rejection means the save failed: the cell then shows the stored value again.
  onCommit: (next: string) => void | Promise<unknown>;
  width?: number | string;
  suggestions?: string[];
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  // The input is uncontrolled (typing must not re-render the grid), so keep it in step with the
  // stored value whenever that changes, including after a failed save.
  useEffect(() => {
    if (inputRef.current && inputRef.current.value !== value) inputRef.current.value = value;
  }, [value]);

  const commit = (next: string) => {
    if (next === value) return;
    Promise.resolve(onCommit(next)).catch(() => {
      if (inputRef.current) inputRef.current.value = value;
    });
  };

  const choose = (opt: string) => {
    if (inputRef.current) inputRef.current.value = opt;
    setOpen(false);
    setActive(-1);
    commit(opt);
  };

  const showList = !!suggestions && open;

  const input = (
    <input
      ref={inputRef}
      type="text"
      defaultValue={value}
      aria-label={ariaLabel}
      {...(hint ? { title: hint, 'aria-describedby': DATE_HINT_ID } : {})}
      {...(suggestions
        ? {
            role: 'combobox' as const,
            'aria-expanded': showList,
            'aria-controls': showList ? listId : undefined,
            'aria-autocomplete': 'list' as const,
            'aria-activedescendant': showList && active >= 0 ? `${listId}-${active}` : undefined,
          }
        : {})}
      style={{
        width: width ?? '100%',
        minWidth: width ?? 120,
        backgroundColor: backgroundColor ?? 'transparent',
        border: '1px solid #e1e5e9',
        borderRadius: 4,
        padding: '4px 6px',
        font: 'inherit',
        boxSizing: 'border-box',
      }}
      onFocus={() => suggestions && setOpen(true)}
      onMouseDown={() => suggestions && setOpen(true)}
      onBlur={(e) => {
        setOpen(false);
        setActive(-1);
        commit(e.target.value);
      }}
      onKeyDown={(e) => {
        if (suggestions && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
          e.preventDefault();
          setOpen(true);
          const last = suggestions.length - 1;
          setActive((i) => (e.key === 'ArrowDown' ? (i >= last ? 0 : i + 1) : i <= 0 ? last : i - 1));
          return;
        }
        if (e.key === 'Enter') {
          if (suggestions && open && active >= 0) {
            e.preventDefault();
            choose(suggestions[active]);
            return;
          }
          e.currentTarget.blur();
        }
        if (e.key === 'Escape') {
          setOpen(false);
          setActive(-1);
        }
      }}
    />
  );

  if (!suggestions) return input;

  return (
    <Box sx={{ position: 'relative' }}>
      {input}
      {showList && (
        <Paper
          elevation={3}
          id={listId}
          role="listbox"
          aria-label={`${ariaLabel} suggestions`}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 20,
            minWidth: 220,
            maxHeight: 220,
            overflowY: 'auto',
          }}
        >
          {suggestions.map((opt, i) => (
            <Box
              key={opt}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              component="div"
              onMouseDown={(e: MouseEvent) => e.preventDefault()}
              onClick={() => choose(opt)}
              sx={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                p: '6px 10px',
                cursor: 'pointer',
                fontSize: '13px',
                backgroundColor: i === active ? '#e3eef7' : 'transparent',
                '&:hover': { backgroundColor: '#f2f7fa' },
              }}
            >
              {opt}
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  );
});

// "Open"/"Closed" mirror the dashboard KPI definitions exactly, so matrix filter counts
// always reconcile with the Global KPIs: Closed = completed (green cells); Open = in_progress
// + not_started (amber/red/uncoloured cells). Any other status (blocked/unknown) is neither.
const OPEN_STATUSES = new Set(['in_progress', 'not_started']);
const CLOSED_STATUSES = new Set(['completed']);
const PRIORITY_OPTIONS = ['High', 'Medium', 'Low'];

// Shared by row-level filtering and per-cell rendering so a selected Status filter also
// blanks out non-matching cells within rows that still have at least one matching cell.
function cellMatchesStatusFilter(cell: MatrixCellOut | undefined, statusFilter: Set<string>): boolean {
  if (statusFilter.size === 0) return true;
  const status = cell?.normalized_status ?? '';
  return (statusFilter.has('open') && OPEN_STATUSES.has(status)) || (statusFilter.has('closed') && CLOSED_STATUSES.has(status));
}

// A selected Overdue filter also blanks out non-matching cells within rows that still
// have at least one overdue cell, same pattern as cellMatchesStatusFilter.
function cellMatchesOverdueFilter(cell: MatrixCellOut | undefined, overdueFilter: Set<string>): boolean {
  if (overdueFilter.size === 0) return true;
  return !!cell?.is_overdue;
}

function MatrixMultiSelectFilter({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: { value: string; text: string }[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  const selectedArray = Array.from(selected);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <FormControl size="small" sx={{ minWidth: 170 }}>
        <InputLabel>{label}</InputLabel>
        <Select
          multiple
          label={label}
          value={selectedArray}
          onChange={(e) => {
            const value = e.target.value;
            const next = typeof value === 'string' ? value.split(',') : value;
            onChange(new Set(next as string[]));
          }}
          renderValue={(vals) => ((vals as string[]).length === 0 ? '' : `${(vals as string[]).length} selected`)}
        >
          {options.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              <Checkbox checked={selected.has(opt.value)} size="small" />
              <ListItemText primary={opt.text} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {selected.size > 0 && (
        <IconButton size="small" aria-label={`Clear ${label} filter`} onClick={() => onChange(new Set())}>
          <CloseIcon fontSize="inherit" />
        </IconButton>
      )}
    </Box>
  );
}

// Action titles are stored as one run of text (the workbook's line breaks are lost), so
// paragraphs are recovered from the markers the authors used: runs of 2+ spaces, section
// headings such as "Root Cause:", and the "Team - ..." owner line.
function formatActionText(text: string): string {
  return text
    .replace(/\s{2,}/g, '\n\n')
    .replace(/\s*(Team\s+-\s)/g, '\n\n$1')
    .replace(/\s*(Root Cause:|Resolution & Recommendations:|It is recommended to:|Recommended version:)/g, '\n\n$1')
    .replace(/(Terminals:)(?=\S)/g, '$1\n\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function ActionTextModal({
  title,
  raw,
  allowEmpty,
  onClose,
  onSave,
}: {
  title: string;
  raw?: string;
  /** True where the user may empty the field (incident details). The action title may not be emptied. */
  allowEmpty?: boolean;
  onClose: () => void;
  onSave?: (next: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  // The editor starts from the same formatted text as the view, so line breaks are visible while editing.
  const [draft, setDraft] = useState(title);
  // Incident text is saved as one line: the display line breaks become spaces, so the stored value is unchanged.
  const toSaved = (text: string) => (raw !== undefined ? text.replace(/\n/g, ' ').trim() : text.trim());
  const current = raw !== undefined ? raw.trim() : title.trim();
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth aria-label="Full action text">
      <IconButton
        aria-label="Close"
        onClick={onClose}
        sx={{ position: 'absolute', right: 8, top: 8 }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ pt: 4 }}>
        {editing ? (
          <TextField
            autoFocus
            multiline
            fullWidth
            minRows={10}
            label="Action text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
        ) : (
          <Typography component="div" sx={{ whiteSpace: 'pre-wrap' }}>
            {title.split('\n').map((line, i) => (
              <Box
                key={i}
                component="span"
                sx={{ display: 'block', fontWeight: /^Team\s+-/.test(line) ? 700 : 400 }}
              >
                {line}
              </Box>
            ))}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {editing ? (
          <>
            <Button
              onClick={() => {
                const next = toSaved(draft);
                if ((allowEmpty || next) && next !== current) onSave?.(next);
                onClose();
              }}
            >
              Save
            </Button>
            <Button onClick={onClose}>Cancel</Button>
          </>
        ) : (
          onSave && <Button onClick={() => setEditing(true)}>Edit</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function ActionEditModal({ value, onSave, onClose }: { value: string; onSave: (next: string) => void; onClose: () => void }) {
  const [draft, setDraft] = useState(value);
  const done = () => {
    onSave(draft);
    onClose();
  };
  return (
    <Dialog open onClose={done} maxWidth="sm" fullWidth aria-label="Edit action">
      <IconButton aria-label="Close" onClick={done} sx={{ position: 'absolute', right: 8, top: 8 }}>
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ pt: 4 }}>
        <TextField
          autoFocus
          multiline
          fullWidth
          minRows={10}
          label="Action text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') done();
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={done}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}

function AddActionForm({ onAdd }: { onAdd: (payload: AddActionRequest) => Promise<boolean> }) {
  const [title, setTitle] = useState('');
  const [incidentDetails, setIncidentDetails] = useState('');
  const [haRaisedDate, setHaRaisedDate] = useState('');
  const [classification, setClassification] = useState('');
  const [category, setCategory] = useState('');
  const [priority, setPriority] = useState('Medium');
  const [editingTitle, setEditingTitle] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmedTitle = title.trim();
    if (!trimmedTitle) return;
    setSubmitting(true);
    try {
      const added = await onAdd({
        action_title: trimmedTitle,
        affected_terminal: incidentDetails.trim(),
        ha_raised_date: haRaisedDate.trim(),
        classification: classification.trim(),
        category: category.trim() || 'Unknown',
        priority,
      });
      if (!added) return;
      setTitle('');
      setIncidentDetails('');
      setHaRaisedDate('');
      setClassification('');
      setCategory('');
      setPriority('Medium');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mb: 2 }}>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Action</TableCell>
              <TableCell>Incident Details</TableCell>
              <TableCell>HA Raised Date</TableCell>
              <TableCell>Classification</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>
                <input
                  aria-label="Action"
                  value={title}
                  readOnly
                  onClick={() => setEditingTitle(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      setEditingTitle(true);
                    }
                  }}
                  placeholder="Click to enter the action"
                  style={{ width: '100%', minWidth: 160, padding: '4px 6px', border: '1px solid #e1e5e9', borderRadius: 4, boxSizing: 'border-box' }}
                />
              </TableCell>
              <TableCell>
                <input
                  aria-label="Incident Details"
                  value={incidentDetails}
                  onChange={(e) => setIncidentDetails(e.target.value)}
                  style={{ width: '100%', minWidth: 160, padding: '4px 6px', border: '1px solid #e1e5e9', borderRadius: 4, boxSizing: 'border-box' }}
                />
              </TableCell>
              <TableCell>
                <input
                  aria-label="HA Raised Date"
                  title={DATE_HINT}
                  aria-describedby={DATE_HINT_ID}
                  value={haRaisedDate}
                  onChange={(e) => setHaRaisedDate(e.target.value)}
                  style={{ width: '100%', minWidth: 120, padding: '4px 6px', border: '1px solid #e1e5e9', borderRadius: 4, boxSizing: 'border-box' }}
                />
              </TableCell>
              <TableCell>
                <Select
                  size="small"
                  value={classification}
                  displayEmpty
                  inputProps={{ 'aria-label': 'Classification' }}
                  onChange={(e) => setClassification(e.target.value as string)}
                  sx={{ minWidth: 120 }}
                >
                  <MenuItem value=""><em>Select…</em></MenuItem>
                  {CLASSIFICATION_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </TableCell>
              <TableCell>
                <Select
                  size="small"
                  value={category}
                  displayEmpty
                  inputProps={{ 'aria-label': 'Category' }}
                  onChange={(e) => setCategory(e.target.value as string)}
                  sx={{ minWidth: 140 }}
                >
                  <MenuItem value=""><em>Select…</em></MenuItem>
                  {CATEGORY_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </TableCell>
              <TableCell>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <Select value={priority} onChange={(e) => setPriority(e.target.value as string)} inputProps={{ 'aria-label': 'Priority' }}>
                    <MenuItem value="High">High</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                    <MenuItem value="Low">Low</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell>
                <Button type="submit" variant="contained" size="small" disabled={submitting}>
                  {submitting ? 'Adding…' : 'Add action'}
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      {editingTitle && <ActionEditModal value={title} onSave={setTitle} onClose={() => setEditingTitle(false)} />}
    </Box>
  );
}

// Shown straight after a new Action is saved. The suggested Jira Epic title is pre-filled for
// review. Nothing reaches Jira until "Send to Jira"; Cancel keeps the Action in the register unsent.
export function NewActionJiraDialog({
  open,
  suggestedTitle,
  onCancel,
  onSend,
}: {
  open: boolean;
  suggestedTitle: string;
  onCancel: () => void;
  onSend: (epicTitle: string) => void;
}) {
  const [title, setTitle] = useState(suggestedTitle);
  useEffect(() => {
    if (open) setTitle(suggestedTitle);
  }, [open, suggestedTitle]);
  const trimmed = title.trim();
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth aria-labelledby="new-action-jira-title">
      <DialogContent>
        <Typography id="new-action-jira-title" variant="h6" sx={{ mb: 1 }}>
          Send this action to Jira?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Review the Epic title. The Epic and one Task per terminal will be created in Jira with it.
        </Typography>
        <TextField
          fullWidth
          autoFocus
          label="Epic title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          inputProps={{ maxLength: 120, 'aria-label': 'Epic title' }}
          helperText={trimmed ? `${trimmed.length}/120` : 'A title is required'}
          error={!trimmed}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Not now</Button>
        <Button variant="contained" disabled={!trimmed} onClick={() => onSend(trimmed)}>
          Send to Jira
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Confirmation before a user deletes an Action. Delete only happens on the explicit button.
export function DeleteActionDialog({
  title,
  onCancel,
  onConfirm,
}: {
  title: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog
      open={title !== null}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      aria-labelledby="delete-action-title"
    >
      <DialogContent>
        <Typography id="delete-action-title" variant="h6" sx={{ mb: 1 }}>
          Delete this action?
        </Typography>
        <Typography sx={{ mb: 2 }}>
          <strong>{title}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This removes the action from the register and its terminal cells. It cannot be undone from the dashboard.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button color="error" variant="contained" onClick={onConfirm} autoFocus>
          Delete action
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Who may see and use the delete control. Set REACT_APP_HA_DELETE_ALLOWED_USERS (comma-separated).
// The server enforces the same list; this only controls what is shown.
const DELETE_ALLOWED_USERS = (process.env.REACT_APP_HA_DELETE_ALLOWED_USERS ?? '')
  .split(',')
  .map((e) => e.trim().toLowerCase())
  .filter(Boolean);

export function canUserDelete(email: string, allowed: string[]): boolean {
  return !!email && allowed.map((e) => e.toLowerCase()).includes(email.toLowerCase());
}

export default function TerminalsActionRegister() {
  const { accounts } = useMsal();
  const currentUserEmail = (
    (accounts[0] as { username?: string } | undefined)?.username ?? ''
  ).toLowerCase();
  const canDelete = canUserDelete(currentUserEmail, DELETE_ALLOWED_USERS);

  const [data, setData] = useState<MatrixResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [info, setInfo] = useState<{ text: string; raw?: string; allowEmpty?: boolean; onSave?: (next: string) => void } | null>(null);

  const [priorityFilter, setPriorityFilter] = useState<Set<string>>(new Set());
  const [classificationFilter, setClassificationFilter] = useState<Set<string>>(new Set());
  const [regionFilter, setRegionFilter] = useState<Set<string>>(new Set());
  const [terminalFilter, setTerminalFilter] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set());
  const [overdueFilter, setOverdueFilter] = useState<Set<string>>(new Set());

  // A short, dismissible message for failed saves and background refreshes. It never hides the table.
  const [notice, setNotice] = useState<string | null>(null);
  const dataRef = useRef<MatrixResponse | null>(null);
  dataRef.current = data;

  // The first load shows the full loading state. Later loads (live updates, Refresh) are silent:
  // the table stays mounted, so scroll position, focus and half-typed text are kept.
  const loadData = useCallback(async () => {
    const firstLoad = dataRef.current === null;
    if (firstLoad) setLoading(true);
    try {
      const result = await getMatrix();
      setData(result);
      setError(null);
    } catch (err) {
      const failure = err instanceof ApiError ? err : new Error('Failed to load the action matrix.');
      if (firstLoad) setError(failure);
      else setNotice(`Could not refresh the register: ${failure.message}`);
    } finally {
      if (firstLoad) setLoading(false);
    }
  }, []);

  // Reload when the server reports a Jira or register change, so the page stays current without a refresh.
  useDashboardChanges(() => { void loadData(); });

  useEffect(() => {
    loadData();
  }, [loadData]);

  const describe = (err: unknown, fallback: string): string => {
    const text = err instanceof Error && err.message ? err.message : fallback;
    // A server error carries a reference the user can quote, so it can be found in the log.
    if (err instanceof ApiError && err.status === 500 && err.correlationId) {
      return `${text} Reference: ${err.correlationId}`;
    }
    return text;
  };

  const CONFLICT_MESSAGE = 'This action was changed by someone else. The latest version has been loaded.';
  const isConflict = (err: unknown): boolean => err instanceof ApiError && err.kind === 'conflict';

  // Thrown after the user has been told, so the cell that started the save knows to show the
  // stored value again. Callers that have no cell to reset ignore it.
  class SaveFailed extends Error {}

  const handleEditCell = async (actionId: string, terminal: string, rawValue: string) => {
    try {
      const result = await editMatrixCell({ action_id: actionId, terminal, raw_value: rawValue });
      markOwnSave();
      setData(result);
    } catch (err) {
      setNotice(`Could not save the change: ${describe(err, 'please try again')}`);
      throw new SaveFailed();
    }
  };

  // Optimistic: show a new priority immediately, then save. If the save fails, put back only the
  // priority of that one row, so a failure never undoes another edit that did succeed.
  const handleEditAction = async (payload: Parameters<typeof editMatrixAction>[0]) => {
    const row = dataRef.current?.actions.find((a) => a.action_id === payload.action_id);
    const previousPriority = row?.priority;
    // Every edit says which version of the row it was made against, so the server can refuse it
    // if someone else changed the row in the meantime.
    const request =
      payload.expected_version === undefined && row ? { ...payload, expected_version: row.version } : payload;
    if (payload.priority !== undefined) {
      setData((prev) =>
        prev
          ? {
              ...prev,
              actions: prev.actions.map((a) =>
                a.action_id === payload.action_id ? { ...a, priority: payload.priority as MatrixActionOut['priority'] } : a,
              ),
            }
          : prev,
      );
    }
    try {
      const result = await editMatrixAction(request);
      markOwnSave();
      setData(result);
    } catch (err) {
      if (payload.priority !== undefined && previousPriority !== undefined) {
        setData((prev) =>
          prev
            ? {
                ...prev,
                actions: prev.actions.map((a) =>
                  a.action_id === payload.action_id ? { ...a, priority: previousPriority } : a,
                ),
              }
            : prev,
        );
      }
      if (isConflict(err)) {
        // Someone else saved first. Say so, load the latest version quietly, and do not retry.
        setNotice(CONFLICT_MESSAGE);
        void loadData();
      } else {
        setNotice(`Could not save the change: ${describe(err, 'please try again')}`);
      }
      throw new SaveFailed();
    }
  };

  // Used where there is no input to reset (dropdowns, popups): the failure is already reported.
  const quietly = (save: Promise<unknown>) => {
    save.catch(() => undefined);
  };

  // After a successful add, open the Jira review popup for the new Action (pre-filled).
  const [newJira, setNewJira] = useState<{ id: string; suggested: string } | null>(null);

  // Resolves to false when the add failed, so the form keeps what the user typed.
  const handleAddAction = async (payload: AddActionRequest): Promise<boolean> => {
    try {
      const result = await addMatrixAction(payload);
      markOwnSave();
      setData(result);
      if (result.new_action_id) {
        setNewJira({ id: result.new_action_id, suggested: result.new_action_suggested_title ?? '' });
      }
      return true;
    } catch (err) {
      setNotice(`Could not add the action: ${describe(err, 'please try again')}`);
      return false;
    }
  };

  const confirmNewActionSend = async (epicTitle: string) => {
    if (!newJira) return;
    const { id } = newJira;
    setNewJira(null);
    try {
      await sendActionToJira(id, epicTitle);
      markOwnSave();
      await loadData();
    } catch (err) {
      setNotice(`Could not send the action to Jira: ${describe(err, 'please try again')}`);
    }
  };

  // Delete is two steps: the row's delete icon opens this confirmation; only the dialog's
  // "Delete" button actually calls the API.
  const [pendingDelete, setPendingDelete] = useState<{ id: string; title: string } | null>(null);


  const requestDeleteAction = (actionId: string, actionTitle: string) => {
    setPendingDelete({ id: actionId, title: actionTitle });
  };

  const confirmDeleteAction = async () => {
    if (!pendingDelete) return;
    const { id } = pendingDelete;
    setPendingDelete(null);
    setDeletingId(id);
    try {
      const result = await deleteMatrixAction(id);
      markOwnSave();
      setData(result);
    } catch (err) {
      setNotice(`Could not delete the action: ${describe(err, 'please try again')}`);
    } finally {
      setDeletingId(null);
    }
  };

  const regionOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const t of data?.terminals ?? []) seen.set(t.region_code, t.region_display_name);
    return Array.from(seen, ([value, text]) => ({ value, text }));
  }, [data]);
  const classificationOptions = useMemo(() => {
    const values = new Set<string>();
    for (const a of data?.actions ?? []) if (a.classification) values.add(a.classification);
    return Array.from(values)
      .sort()
      .map((value) => ({ value, text: value }));
  }, [data]);
  const terminalOptions = useMemo(() => {
    const terminals = data?.terminals ?? [];
    const inSelectedRegions = regionFilter.size === 0 ? terminals : terminals.filter((t) => regionFilter.has(t.region_code));
    return inSelectedRegions.map((t) => ({ value: t.terminal_name, text: t.terminal_name }));
  }, [data, regionFilter]);

  const visibleTerminals = useMemo(() => {
    return (data?.terminals ?? []).filter((t) => {
      if (regionFilter.size > 0 && !regionFilter.has(t.region_code)) return false;
      if (terminalFilter.size > 0 && !terminalFilter.has(t.terminal_name)) return false;
      return true;
    });
  }, [data, regionFilter, terminalFilter]);
  const regionGroups = useMemo(() => groupByRegion<MatrixTerminalOut>(visibleTerminals), [visibleTerminals]);

  const visibleActions = useMemo(() => {
    return (data?.actions ?? []).filter((action) => {
      if (priorityFilter.size > 0 && !priorityFilter.has(action.priority)) return false;
      if (classificationFilter.size > 0 && !classificationFilter.has(action.classification ?? '')) return false;
      if (statusFilter.size > 0) {
        const matches = visibleTerminals.some((t) => cellMatchesStatusFilter(action.cells[t.terminal_name], statusFilter));
        if (!matches) return false;
      }
      if (overdueFilter.size > 0) {
        const matches = visibleTerminals.some((t) => cellMatchesOverdueFilter(action.cells[t.terminal_name], overdueFilter));
        if (!matches) return false;
      }
      return true;
    });
  }, [data, priorityFilter, classificationFilter, statusFilter, overdueFilter, visibleTerminals]);

  const anyFilterActive =
    priorityFilter.size > 0 ||
    classificationFilter.size > 0 ||
    regionFilter.size > 0 ||
    terminalFilter.size > 0 ||
    statusFilter.size > 0 ||
    overdueFilter.size > 0;

  return (
    <Box sx={{ p: 3 }}>
      <TerminalsHeader title="Terminals — Action Register" onRefreshed={loadData} />

      {loading && !data && <TerminalsLoadingState label="Loading action matrix…" />}
      {error && !data && <TerminalsErrorState error={error} onRetry={loadData} />}

      {data && (
        <>
          <AddActionForm onAdd={handleAddAction} />

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
            <MatrixMultiSelectFilter label="Region" options={regionOptions} selected={regionFilter} onChange={setRegionFilter} />
            <MatrixMultiSelectFilter label="Terminal" options={terminalOptions} selected={terminalFilter} onChange={setTerminalFilter} />
            <MatrixMultiSelectFilter
              label="Priority"
              options={PRIORITY_OPTIONS.map((p) => ({ value: p, text: p }))}
              selected={priorityFilter}
              onChange={setPriorityFilter}
            />
            <MatrixMultiSelectFilter
              label="Classification"
              options={classificationOptions}
              selected={classificationFilter}
              onChange={setClassificationFilter}
            />
            <MatrixMultiSelectFilter
              label="Status"
              options={[
                { value: 'open', text: 'Open Actions' },
                { value: 'closed', text: 'Closed Actions' },
              ]}
              selected={statusFilter}
              onChange={setStatusFilter}
            />
            <MatrixMultiSelectFilter
              label="Overdue"
              options={[{ value: 'overdue', text: 'Overdue actions' }]}
              selected={overdueFilter}
              onChange={setOverdueFilter}
            />
            {anyFilterActive && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setPriorityFilter(new Set());
                  setClassificationFilter(new Set());
                  setRegionFilter(new Set());
                  setTerminalFilter(new Set());
                  setStatusFilter(new Set());
                  setOverdueFilter(new Set());
                }}
              >
                Clear all filters
              </Button>
            )}
          </Box>

          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '72vh', overflow: 'auto' }}>
            <Table size="small" stickyHeader aria-label="Editable action-by-terminal matrix, matching the source workbook layout">
              <TableHead>
                <TableRow>
                  <TableCell rowSpan={2}>Action</TableCell>
                  <TableCell rowSpan={2}>Incident Details</TableCell>
                  <TableCell rowSpan={2}>HA Raised Date</TableCell>
                  <TableCell rowSpan={2}>Classification</TableCell>
                  <TableCell rowSpan={2}>Category</TableCell>
                  <TableCell rowSpan={2}>Priority</TableCell>
                  <TableCell rowSpan={2} />
                  {regionGroups.map((g) => (
                    <TableCell
                      key={g.regionCode}
                      colSpan={g.items.length}
                      align="center"
                      sx={{ backgroundColor: '#f2f7fa', fontWeight: 700 }}
                    >
                      {g.regionDisplayName}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  {regionGroups.flatMap((g) =>
                    g.items.map((t) => (
                      <TableCell key={t.terminal_name} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {t.terminal_name}
                      </TableCell>
                    )),
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleActions.map((action) => (
                  <TableRow key={action.action_id} hover>
                    <TableCell sx={{ minWidth: 220 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          aria-label={`Show full text for ${action.action_title}`}
                          onClick={() =>
                            setInfo({
                              text: formatActionText(action.action_title),
                              onSave: (next) => quietly(handleEditAction({ action_id: action.action_id, action_title: next })),
                            })
                          }
                        >
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                        <Box sx={{ flex: 1 }}>
                          <EditableTextCell
                            value={action.action_title}
                            ariaLabel={`Action title: ${action.action_title}`}
                            onCommit={(next) => {
                              if (!next.trim()) return Promise.reject(new Error('blank title'));
                              return handleEditAction({ action_id: action.action_id, action_title: next });
                            }}
                          />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 200 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          aria-label={`Show full incident details for ${action.action_title}`}
                          onClick={() => {
                            setInfo({
                              text: incidentDetailsText(action),
                              onSave: (next) => quietly(handleEditAction({ action_id: action.action_id, affected_terminal: next })),
                              // Saves start from the stored value, not the display text with added line breaks.
                              raw: action.affected_terminal ?? '',
                              allowEmpty: true,
                            });
                          }}
                        >
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                        <Box sx={{ flex: 1 }}>
                          <EditableTextCell
                            value={action.affected_terminal ?? ''}
                            ariaLabel={`Incident details for ${action.action_title}`}
                            onCommit={(next) => handleEditAction({ action_id: action.action_id, affected_terminal: next })}
                          />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <EditableTextCell
                        value={action.ha_raised_date ?? ''}
                        ariaLabel={`HA raised date for ${action.action_title}`}
                        hint={DATE_HINT}
                        width={110}
                        onCommit={(next) => handleEditAction({ action_id: action.action_id, ha_raised_date: next })}
                      />
                    </TableCell>
                    <TableCell>
                      <ChoiceCell
                        value={action.classification ?? ''}
                        options={CLASSIFICATION_OPTIONS}
                        ariaLabel={`Classification for ${action.action_title}`}
                        onCommit={(next) => handleEditAction({ action_id: action.action_id, classification: next })}
                      />
                    </TableCell>
                    <TableCell>
                      <ChoiceCell
                        value={action.category}
                        options={CATEGORY_OPTIONS}
                        ariaLabel={`Category for ${action.action_title}`}
                        onCommit={(next) => handleEditAction({ action_id: action.action_id, category: next })}
                      />
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select
                          value={action.priority}
                          inputProps={{ 'aria-label': `Priority for ${action.action_title}` }}
                          onChange={(e) => quietly(handleEditAction({ action_id: action.action_id, priority: e.target.value as string }))}
                        >
                          <MenuItem value="High">High</MenuItem>
                          <MenuItem value="Medium">Medium</MenuItem>
                          <MenuItem value="Low">Low</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      {canDelete && (
                      <IconButton
                        size="small"
                        color="error"
                        disabled={deletingId === action.action_id}
                        aria-label={`Delete action: ${action.action_title}`}
                        onClick={() => requestDeleteAction(action.action_id, action.action_title)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                      )}
                    </TableCell>
                    {regionGroups.flatMap((g) =>
                      g.items.map((t) => {
                        const cell = action.cells[t.terminal_name];
                        if (!cellMatchesStatusFilter(cell, statusFilter) || !cellMatchesOverdueFilter(cell, overdueFilter)) {
                          return <TableCell key={t.terminal_name} aria-hidden="true" />;
                        }
                        return (
                          <TableCell key={t.terminal_name} sx={{ minWidth: 150 }}>
                            {/* The coloured value cell always stays; the Jira link sits beneath it. */}
                            <EditableTextCell
                              value={cell?.raw_value ?? ''}
                              ariaLabel={`${action.action_title} — ${t.terminal_name}`}
                              backgroundColor={resolveCellBackground(cell)}
                              suggestions={MATRIX_CELL_VALUE_OPTIONS}
                              onCommit={(next) => handleEditCell(action.action_id, t.terminal_name, next)}
                            />
                            <JiraCellLink cell={cell} />
                          </TableCell>
                        );
                      }),
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Box id={DATE_HINT_ID} sx={{ position: 'absolute', left: -9999 }}>
        {DATE_HINT}
      </Box>

      <Snackbar
        open={notice !== null}
        autoHideDuration={8000}
        onClose={(_, reason) => { if (reason !== 'clickaway') setNotice(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" role="alert" variant="filled" onClose={() => setNotice(null)} sx={{ width: '100%' }}>
          {notice}
        </Alert>
      </Snackbar>

      {info && <ActionTextModal title={info.text} raw={info.raw} allowEmpty={info.allowEmpty} onSave={info.onSave} onClose={() => setInfo(null)} />}


      <NewActionJiraDialog
        open={newJira !== null}
        suggestedTitle={newJira?.suggested ?? ''}
        onCancel={() => setNewJira(null)}
        onSend={confirmNewActionSend}
      />

      <DeleteActionDialog
        title={pendingDelete?.title ?? null}
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmDeleteAction}
      />
    </Box>
  );
}
